import { FormFieldInputType } from 'models/enum';

export enum SectionTitle {
  EMERGENCY_DEPARTMENT = 'Emergency Department'
}

export enum ViewTitle {
  EMERGENCY_DEPARTMENT = 'Emergency Department'
}

export enum FormField {
  // Step 1 - Patient Details
  PATIENT_NAME = 'patientName',
  GENDER = 'gender',
  AGE = 'age',
  MODE_OF_ARRIVAL = 'modeOfArrival',
  REFERRING_FACILITY = 'referringFacility',
  TELEPHONE_PAGER_NUMBER = 'telephonePagerNumber',
  CHIEF_COMPLAINT = 'chiefComplaint',
  BLOOD_PRESSURE = 'bloodPressure',
  RESPIRATION = 'respiration',
  PULSE = 'pulse',
  TEMPERATURE = 'temperature',
  WEIGHT = 'weight',
  INITIAL_ASSESSMENT = 'initialAssessment',
  CARE_PRIOR_TO_ARRIVAL = 'carePriorToArrival',
  MEDICAL_HISTORY = 'medicalHistory',
  PRIMARY_PROBLEM = 'primaryProblem',
  SECONDARY_PROBLEM = 'secondaryProblem',

  // Step 3 - Review of Systems / Neurological
  ORIENTATION = 'orientation',
  MOVING_ALL_EXTREMITIES = 'movingAllExtremities',
  EMOTIONAL_STATUS = 'emotionalStatus',
  NEUROLOGICAL_VITAL_SIGNS_RECORD = 'neurologicalVitalSignsRecord',
  UPPER_EXTREMITY_STRENGTH = 'upperExtremityStrength',
  LOWER_EXTREMITY_STRENGTH = 'lowerExtremityStrength',
  // Cardiovascular
  CARDIOVASCULAR_PULSE = 'cardiovascularPulse',
  RHYTHM = 'rhythm',
  EDEMA = 'edema',
  CARDIOVASCULAR_LOCATION = 'cardiovascularLocation',
  CAPILLARY_REFILL = 'capillaryRefill',
  PERIPHERAL_PULSES_RADIAL = 'peripheralPulsesRadial',
  PERIPHERAL_PULSES_PEDAL = 'peripheralPulsesPedal',
  // Respiratory
  RESPIRATORY_EFFORT = 'respiratoryEffort',
  BREATH_SOUNDS = 'breathSounds',
  // Integumentary
  SKIN = 'skin',
  TURGOR = 'turgor',
  MUCOUS_MEMBRANES = 'mucousMembranes',
  ANTERIOR_FONTANEL = 'anteriorFontanel',
  // Gastrointestinal
  GASTROINTESTINAL_NOT_APPLICABLE = 'gastrointestinalNotApplicable',
  ABDOMEN = 'abdomen',
  BOWEL_SOUNDS = 'bowelSounds',
  BOWEL_SOUND_DESCRIPTION = 'bowelSoundDescription',
  STOOL = 'stool',
  STOOL_DESCRIPTION = 'stoolDescription',
  LAST_BM = 'lastBm',
  // Genitourinary
  GENITOURINARY_NOT_APPLICABLE = 'genitourinaryNotApplicable',
  URINE_OUTPUT = 'urineOutput',
  VAGINAL_BLEEDING = 'vaginalBleeding',
  DISCHARGE = 'discharge',
  PAD_PER_DAY = 'padPerDay',
  // Reproductive
  REPRODUCTIVE_NOT_APPLICABLE = 'reproductiveNotApplicable',
  PREGNANT = 'pregnant',
  EXPECTED_DUE_DATE = 'expectedDueDate',
  FETAL_ACTIVITY = 'fetalActivity',
  BLOOD_TYPE = 'bloodType',
  RH_FACTOR = 'rhFactor',
  // Drug Use
  DRUG_USE = 'drugUse',

  // Step 5 - ED Notes (Figma frame 11772:22725)
  NOTES = 'notes',
  PROVIDER_SIGNATURE = 'providerSignature',
  SIGNATURE_DAY = 'day',
  SIGNATURE_TIME = 'time'
}

export interface EDFieldConfig {
  name: FormField;
  label: string;
  type: FormFieldInputType;
  maxLength?: number;
}

export interface EDSectionConfig {
  title: string;
  rows: FormField[][];
}

/** A dynamic table populated via an "Add" modal (rows serialized into the fragment). */
export enum CollectionKey {
  MEDICATION = 'medication',
  ALLERGY = 'allergy',
  LABORATORY = 'laboratory',
  VITAL_SIGNS = 'vitalSigns'
}

export interface EDCollectionColumn {
  key: string;
  label: string;
}

export interface EDCollectionConfig {
  key: CollectionKey;
  title: string;
  modalTitle: string;
  columns: EDCollectionColumn[];
}

export enum RepeatableKey {
  PAIN = 'pain',
  OXYGEN_SATURATION = 'oxygenSaturation',
  PUPILS = 'pupils',
  GLASGOW_COMA = 'glasgowComa'
}

export interface EDRepeatableField {
  key: string;
  label: string;
  type: FormFieldInputType;
}

export interface EDRepeatableConfig {
  key: RepeatableKey;
  title: string;
  fields: EDRepeatableField[];
}

export interface EDStepConfig {
  title: string;
  sections?: EDSectionConfig[];
  collections?: CollectionKey[];
  repeatables?: RepeatableKey[];
}

const DEFAULT_TEXT_INPUT_MAX_LENGTH = 150;
const DEFAULT_TEXT_AREA_MAX_LENGTH = 500;
const NOT_APPLICABLE_LABEL = 'Not Applicable';

const withDefaultMaxLength = (fieldConfig: EDFieldConfig): EDFieldConfig => {
  if (fieldConfig.maxLength) return fieldConfig;
  if (fieldConfig.type === FormFieldInputType.TEXT_BOX) return { ...fieldConfig, maxLength: DEFAULT_TEXT_INPUT_MAX_LENGTH };
  if (fieldConfig.type === FormFieldInputType.TEXT_AREA) return { ...fieldConfig, maxLength: DEFAULT_TEXT_AREA_MAX_LENGTH };
  return fieldConfig;
};

export const FIELD_CONFIGS: EDFieldConfig[] = [
  // Step 1 - Patient Details
  { name: FormField.PATIENT_NAME, label: 'Patient Name', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.GENDER, label: 'Gender', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.AGE, label: 'Age', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MODE_OF_ARRIVAL, label: 'Mode of Arrival', type: FormFieldInputType.DROPDOWN },
  { name: FormField.REFERRING_FACILITY, label: 'Referring Facility', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.TELEPHONE_PAGER_NUMBER, label: 'Telephone/Pager Number', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CHIEF_COMPLAINT, label: 'Chief Complaint', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.BLOOD_PRESSURE, label: 'Blood Pressure', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.RESPIRATION, label: 'Respiration', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PULSE, label: 'Pulse', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.TEMPERATURE, label: 'Temperature', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.WEIGHT, label: 'Weight', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.INITIAL_ASSESSMENT, label: 'Initial Assessment', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.CARE_PRIOR_TO_ARRIVAL, label: 'Care Prior to Arrival', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.MEDICAL_HISTORY, label: 'Medical History', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.PRIMARY_PROBLEM, label: 'Primary Problem', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.SECONDARY_PROBLEM, label: 'Secondary Problem', type: FormFieldInputType.TEXT_AREA },

  // Step 3 - Review of Systems / Neurological
  { name: FormField.ORIENTATION, label: 'Orientation', type: FormFieldInputType.DROPDOWN },
  { name: FormField.MOVING_ALL_EXTREMITIES, label: 'Moving All Extremities', type: FormFieldInputType.DROPDOWN },
  { name: FormField.EMOTIONAL_STATUS, label: 'Emotional Status', type: FormFieldInputType.DROPDOWN },
  { name: FormField.NEUROLOGICAL_VITAL_SIGNS_RECORD, label: 'Neurological Vital Signs Record', type: FormFieldInputType.DROPDOWN },
  { name: FormField.UPPER_EXTREMITY_STRENGTH, label: 'Upper Extremity Strength', type: FormFieldInputType.DROPDOWN },
  { name: FormField.LOWER_EXTREMITY_STRENGTH, label: 'Lower Extremity Strength', type: FormFieldInputType.DROPDOWN },
  // Cardiovascular
  { name: FormField.CARDIOVASCULAR_PULSE, label: 'Pulse', type: FormFieldInputType.DROPDOWN },
  { name: FormField.RHYTHM, label: 'Rhythm', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EDEMA, label: 'Edema', type: FormFieldInputType.DROPDOWN },
  { name: FormField.CARDIOVASCULAR_LOCATION, label: 'Location', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CAPILLARY_REFILL, label: 'Capillary Refill', type: FormFieldInputType.DROPDOWN },
  { name: FormField.PERIPHERAL_PULSES_RADIAL, label: 'Peripheral Pulses Location: Radial', type: FormFieldInputType.DROPDOWN },
  { name: FormField.PERIPHERAL_PULSES_PEDAL, label: 'Peripheral Pulses Location: Pedal', type: FormFieldInputType.DROPDOWN },
  // Respiratory
  { name: FormField.RESPIRATORY_EFFORT, label: 'Effort', type: FormFieldInputType.DROPDOWN },
  { name: FormField.BREATH_SOUNDS, label: 'Breath Sounds', type: FormFieldInputType.TEXT_BOX },
  // Integumentary
  { name: FormField.SKIN, label: 'Skin', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.TURGOR, label: 'Turgor', type: FormFieldInputType.DROPDOWN },
  { name: FormField.MUCOUS_MEMBRANES, label: 'Mucous Membranes', type: FormFieldInputType.DROPDOWN },
  { name: FormField.ANTERIOR_FONTANEL, label: 'Anterior Fontanel', type: FormFieldInputType.DROPDOWN },
  // Gastrointestinal
  { name: FormField.GASTROINTESTINAL_NOT_APPLICABLE, label: NOT_APPLICABLE_LABEL, type: FormFieldInputType.CHECK_BOX },
  { name: FormField.ABDOMEN, label: 'Abdomen', type: FormFieldInputType.DROPDOWN },
  { name: FormField.BOWEL_SOUNDS, label: 'Bowel Sounds', type: FormFieldInputType.DROPDOWN },
  { name: FormField.BOWEL_SOUND_DESCRIPTION, label: 'Bowel Sound Description', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.STOOL, label: 'Stool', type: FormFieldInputType.DROPDOWN },
  { name: FormField.STOOL_DESCRIPTION, label: 'Stool Description', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.LAST_BM, label: 'Last BM', type: FormFieldInputType.TEXT_BOX },
  // Genitourinary
  { name: FormField.GENITOURINARY_NOT_APPLICABLE, label: NOT_APPLICABLE_LABEL, type: FormFieldInputType.CHECK_BOX },
  { name: FormField.URINE_OUTPUT, label: 'Urine Output', type: FormFieldInputType.DROPDOWN },
  { name: FormField.VAGINAL_BLEEDING, label: 'Vaginal Bleeding', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.DISCHARGE, label: 'Discharge', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PAD_PER_DAY, label: 'Pad Per Day', type: FormFieldInputType.TEXT_BOX },
  // Reproductive
  { name: FormField.REPRODUCTIVE_NOT_APPLICABLE, label: NOT_APPLICABLE_LABEL, type: FormFieldInputType.CHECK_BOX },
  { name: FormField.PREGNANT, label: 'Pregnant', type: FormFieldInputType.DROPDOWN },
  { name: FormField.EXPECTED_DUE_DATE, label: 'If Pos, Expected Due Date', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.FETAL_ACTIVITY, label: 'Fetal Activity', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.BLOOD_TYPE, label: 'Blood Type', type: FormFieldInputType.DROPDOWN },
  { name: FormField.RH_FACTOR, label: 'Rh Factor', type: FormFieldInputType.TEXT_BOX },
  // Drug Use
  { name: FormField.DRUG_USE, label: 'ETOH / Street Drugs / Overdose', type: FormFieldInputType.TEXT_AREA },

  // Step 5 - ED Notes (Figma frame 11772:22725)
  { name: FormField.NOTES, label: 'Notes', type: FormFieldInputType.TEXT_AREA },
  { name: FormField.PROVIDER_SIGNATURE, label: "Provider's Signature", type: FormFieldInputType.TEXT_BOX },
  { name: FormField.SIGNATURE_DAY, label: 'Day', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.SIGNATURE_TIME, label: 'Time', type: FormFieldInputType.TIME }
].map(withDefaultMaxLength);

export const FIELD_CONFIG_MAP: Map<FormField, EDFieldConfig> = new Map(FIELD_CONFIGS.map((config) => [config.name, config]));

export const NOT_APPLICABLE_SECTIONS: { checkbox: FormField; fields: FormField[] }[] = [
  {
    checkbox: FormField.GASTROINTESTINAL_NOT_APPLICABLE,
    fields: [FormField.ABDOMEN, FormField.BOWEL_SOUNDS, FormField.BOWEL_SOUND_DESCRIPTION, FormField.STOOL, FormField.STOOL_DESCRIPTION, FormField.LAST_BM]
  },
  {
    checkbox: FormField.GENITOURINARY_NOT_APPLICABLE,
    fields: [FormField.URINE_OUTPUT, FormField.VAGINAL_BLEEDING, FormField.DISCHARGE, FormField.PAD_PER_DAY]
  },
  {
    checkbox: FormField.REPRODUCTIVE_NOT_APPLICABLE,
    fields: [FormField.PREGNANT, FormField.EXPECTED_DUE_DATE, FormField.FETAL_ACTIVITY, FormField.BLOOD_TYPE, FormField.RH_FACTOR]
  }
];

const buildNotApplicableControllerMap = (): Map<FormField, FormField> => {
  const controllerMap = new Map<FormField, FormField>();
  NOT_APPLICABLE_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => controllerMap.set(field, section.checkbox));
  });
  return controllerMap;
};

export const FIELD_TO_NOT_APPLICABLE_CONTROLLER: Map<FormField, FormField> = buildNotApplicableControllerMap();

export const COLLECTION_CONFIGS: Record<CollectionKey, EDCollectionConfig> = {
  [CollectionKey.MEDICATION]: {
    key: CollectionKey.MEDICATION,
    title: 'Medication',
    modalTitle: 'Add Medications',
    columns: [
      { key: 'medication', label: 'Medication' },
      { key: 'dose', label: 'Dose' },
      { key: 'frequency', label: 'Frequency' },
      { key: 'lastDose', label: 'Last Dose' }
    ]
  },
  [CollectionKey.ALLERGY]: {
    key: CollectionKey.ALLERGY,
    title: 'Allergy',
    modalTitle: 'Add Allergies',
    columns: [
      { key: 'allergy', label: 'Allergy' },
      { key: 'reaction', label: 'Reaction' }
    ]
  },
  [CollectionKey.LABORATORY]: {
    key: CollectionKey.LABORATORY,
    title: 'Laboratory',
    modalTitle: 'Add Laboratory',
    columns: [
      { key: 'test', label: 'Test' },
      { key: 'timeDrawn', label: 'Time Drawn' },
      { key: 'timeResulted', label: 'Time Resulted' },
      { key: 'results', label: 'Results' }
    ]
  },
  [CollectionKey.VITAL_SIGNS]: {
    key: CollectionKey.VITAL_SIGNS,
    title: 'Vital Signs',
    modalTitle: 'Add Vital Signs',
    columns: [
      { key: 'time', label: 'Time' },
      { key: 'temperature', label: 'Temperature' },
      { key: 'bloodPressure', label: 'Blood Pressure' },
      { key: 'pulse', label: 'Pulse' },
      { key: 'respirations', label: 'Respirations' }
    ]
  }
};

export const REPEATABLE_CONFIGS: Record<RepeatableKey, EDRepeatableConfig> = {
  [RepeatableKey.PAIN]: {
    key: RepeatableKey.PAIN,
    title: 'Pain',
    fields: [
      { key: 'time', label: 'Time', type: FormFieldInputType.TIME },
      { key: 'scale', label: 'Scale', type: FormFieldInputType.TEXT_BOX },
      { key: 'rating', label: 'Rating', type: FormFieldInputType.TEXT_BOX }
    ]
  },
  [RepeatableKey.OXYGEN_SATURATION]: {
    key: RepeatableKey.OXYGEN_SATURATION,
    title: 'Oxygen Saturation',
    fields: [
      { key: 'time', label: 'Time', type: FormFieldInputType.TIME },
      { key: 'mode', label: 'Mode', type: FormFieldInputType.TEXT_BOX },
      { key: 'fiO2', label: '% or FiO2', type: FormFieldInputType.TEXT_BOX }
    ]
  },
  [RepeatableKey.PUPILS]: {
    key: RepeatableKey.PUPILS,
    title: 'Pupils',
    fields: [
      { key: 'time', label: 'Time', type: FormFieldInputType.TIME },
      { key: 'left', label: 'Left', type: FormFieldInputType.TEXT_BOX },
      { key: 'right', label: 'Right', type: FormFieldInputType.TEXT_BOX }
    ]
  },
  [RepeatableKey.GLASGOW_COMA]: {
    key: RepeatableKey.GLASGOW_COMA,
    title: 'Glasgow Coma',
    fields: [
      { key: 'time', label: 'Time', type: FormFieldInputType.TIME },
      { key: 'score', label: 'Score', type: FormFieldInputType.TEXT_BOX }
    ]
  }
};

export const STEP_CONFIGS: EDStepConfig[] = [
  {
    title: 'Patient Details',
    sections: [
      {
        title: 'Patient Details',
        rows: [
          [FormField.PATIENT_NAME, FormField.GENDER, FormField.AGE],
          [FormField.MODE_OF_ARRIVAL, FormField.REFERRING_FACILITY, FormField.TELEPHONE_PAGER_NUMBER]
        ]
      },
      {
        title: '',
        rows: [
          [FormField.CHIEF_COMPLAINT],
          [FormField.BLOOD_PRESSURE, FormField.RESPIRATION, FormField.PULSE],
          [FormField.TEMPERATURE, FormField.WEIGHT],
          [FormField.INITIAL_ASSESSMENT],
          [FormField.CARE_PRIOR_TO_ARRIVAL],
          [FormField.MEDICAL_HISTORY],
          [FormField.PRIMARY_PROBLEM],
          [FormField.SECONDARY_PROBLEM]
        ]
      }
    ]
  },
  {
    title: 'Medications & Allergies',
    collections: [CollectionKey.MEDICATION, CollectionKey.ALLERGY]
  },
  {
    title: 'Review of Systems',
    sections: [
      {
        title: 'Neurological',
        rows: [
          [FormField.ORIENTATION, FormField.MOVING_ALL_EXTREMITIES, FormField.EMOTIONAL_STATUS],
          [FormField.NEUROLOGICAL_VITAL_SIGNS_RECORD, FormField.UPPER_EXTREMITY_STRENGTH, FormField.LOWER_EXTREMITY_STRENGTH]
        ]
      },
      {
        title: 'Cardiovascular',
        rows: [
          [FormField.CARDIOVASCULAR_PULSE, FormField.RHYTHM, FormField.EDEMA],
          [FormField.CARDIOVASCULAR_LOCATION, FormField.CAPILLARY_REFILL, FormField.PERIPHERAL_PULSES_RADIAL],
          [FormField.PERIPHERAL_PULSES_PEDAL]
        ]
      },
      {
        title: 'Respiratory',
        rows: [[FormField.RESPIRATORY_EFFORT, FormField.BREATH_SOUNDS]]
      },
      {
        title: 'Integumentary',
        rows: [[FormField.SKIN, FormField.TURGOR, FormField.MUCOUS_MEMBRANES], [FormField.ANTERIOR_FONTANEL]]
      },
      {
        title: 'Gastrointestinal',
        rows: [
          [FormField.GASTROINTESTINAL_NOT_APPLICABLE],
          [FormField.ABDOMEN, FormField.BOWEL_SOUNDS, FormField.BOWEL_SOUND_DESCRIPTION],
          [FormField.STOOL, FormField.STOOL_DESCRIPTION, FormField.LAST_BM]
        ]
      },
      {
        title: 'Genitourinary',
        rows: [[FormField.GENITOURINARY_NOT_APPLICABLE], [FormField.URINE_OUTPUT, FormField.VAGINAL_BLEEDING, FormField.DISCHARGE], [FormField.PAD_PER_DAY]]
      },
      {
        title: 'Reproductive',
        rows: [[FormField.REPRODUCTIVE_NOT_APPLICABLE], [FormField.PREGNANT, FormField.EXPECTED_DUE_DATE, FormField.FETAL_ACTIVITY], [FormField.BLOOD_TYPE, FormField.RH_FACTOR]]
      },
      {
        title: 'Drug Use',
        rows: [[FormField.DRUG_USE]]
      }
    ]
  },
  {
    title: 'Labs & Vital Signs',
    collections: [CollectionKey.LABORATORY, CollectionKey.VITAL_SIGNS],
    repeatables: [RepeatableKey.PAIN, RepeatableKey.OXYGEN_SATURATION, RepeatableKey.PUPILS, RepeatableKey.GLASGOW_COMA]
  },
  {
    title: 'ED Notes',
    sections: [
      {
        title: 'ED Notes',
        rows: [[FormField.NOTES], [FormField.PROVIDER_SIGNATURE, FormField.SIGNATURE_DAY, FormField.SIGNATURE_TIME]]
      }
    ]
  }
];

export const STEP_TITLES: string[] = STEP_CONFIGS.map((step) => step.title);
